import React, { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { ITestable } from "@/browser";
import { Button } from "@/components/Button";
import { LazyImg } from "@/components/Fetcher";
import { Icon } from "@/components/Icon";
import { Separator } from "@/components/Separator";
import { Text } from "@/components/Text";
import { IMAGES_EXTENSIONS } from "@/consts/fileExtensions";
import { IAttachment, TAttachmentPurpose } from "@/data/attachments";
import { Flex, IChangeable, Img } from "@/eworkitComponents";
import { withFormikField } from "@/formikComponents";
import { FormikFileUpload } from "@/views/components/Formik";

import { ProgressAttachment } from "../Attachment/ProgressAttachment";
import { FileButton } from "../FileButton";

import "./_image-section.scss";

interface IImageSectionProps extends IChangeable, ITestable {
  name: string;
  value: IAttachment | null;
  attachmentType: TAttachmentPurpose;
  renderPlaceholder: () => ReactNode;
  renderImage?: () => ReactNode;
  renderDetails?: () => ReactNode;
}

const ImageSectionComponent: FC<IImageSectionProps> = ({
  name,
  attachmentType,
  renderPlaceholder,
  renderImage,
  renderDetails,
  ...restProps
}) => {
  const { t } = useTranslation("session");

  const baseClassName = "image-section";

  return (
    <FormikFileUpload name={name} value={restProps.value}>
      {({ value, onFileChange, onFileBlur }) => (
        <Flex
          className={`${baseClassName}__image-section`}
          padding={["md", "md", false, "md"]}
          direction="column"
          innerMargin="md"
          align="center"
          justify="center">
          {value && value.attachment && (
            <LazyImg key={value.attachment.id} systemId={value.attachment.systemId}>
              {({ payload, error }) =>
                !payload || !payload.publicUrl || error ? (
                  renderPlaceholder()
                ) : (
                  <div className={`${baseClassName}__image-container`}>
                    <div className={`${baseClassName}__delete-button-container`}>
                      <Button variant="clean" size="md" onClick={() => value.onRemove(null)} testName="delete">
                        <Icon icon="close" color="background-card" size="md" />
                      </Button>
                    </div>

                    {renderImage ? renderImage() : <Img src={payload.publicUrl} responsive="width" />}
                  </div>
                )
              }
            </LazyImg>
          )}

          {value && value.file && (
            <Flex direction="column" align="center" justify="center" width={12}>
              <ProgressAttachment
                key={value.key}
                className={`${baseClassName}__progress-attachment`}
                file={value.file}
                onUpload={newValue => {
                  value.onUpload(newValue);
                }}
                type={attachmentType}
                maxSizeMb={1}
                overrideMessages={{
                  FILE_TOO_BIG: t("COMPONENTS.IMAGE_SECTION.FILE_TOO_BIG", { size: 1 }),
                }}
              />
            </Flex>
          )}

          {!value && renderPlaceholder()}

          <Flex direction="column">
            <Text color="grey1" size="md" align="center">
              {t("COMPONENTS.IMAGE_SECTION.FORMAT", { size: 1 })}
            </Text>

            {renderDetails && renderDetails()}
          </Flex>

          <Separator />

          <FileButton onChange={onFileChange} onBlur={onFileBlur} extensions={IMAGES_EXTENSIONS}>
            {t("COMPONENTS.IMAGE_SECTION.BUTTON_UPLOAD")}
          </FileButton>
        </Flex>
      )}
    </FormikFileUpload>
  );
};

export const ImageSection = withFormikField(ImageSectionComponent);
